import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyAPI } from '../services/propertyAPI';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../contexts/ToastContext';
import { formatPrice, getLabel, PROPERTY_TYPES } from '../utils/propertyConstants';

const MyProperties = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    fetchMyProperties();
    // eslint-disable-next-line
  }, [currentPage]);

  const fetchMyProperties = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await propertyAPI.getMyProperties(currentPage);
      
      if (response.success) {
        setProperties(response.data);
        setPagination(response.pagination);
      }
    } catch (err) {
      setError('Failed to fetch your properties.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteLoading(propertyId);
      const response = await propertyAPI.deleteProperty(propertyId);
      
      showSuccess(response.message || 'Property deleted successfully');
      // Refresh the list
      fetchMyProperties();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to delete property. Please try again.');
      console.error(err);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleEdit = (propertyId) => {
    navigate(`/edit-property/${propertyId}`);
  };

  return (
    <div className="min-h-screen gradient-bg-light pt-20">
      {/* Fixed Header */}
      <Header transparent={true} />

      {/* Page Title */}
      <div className="glass backdrop-blur-lg border-b border-purple-100 py-4 fixed top-[80px] left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-gray-900">My Properties</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your listed properties</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {!loading && !error && properties.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No properties yet</h3>
            <p className="text-gray-600 mb-4">Start by adding your first property</p>
            <button
              onClick={() => navigate('/create-property')}
              className="px-6 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition"
            >
              Add Property
            </button>
          </div>
        )}

        {!loading && !error && properties.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-6">
              {properties.map((property, index) => (
                <div 
                  key={property._id || property.id} 
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 fade-in"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="md:flex relative">
                    {/* Image */}
                    <div className="md:w-64 h-48 md:h-auto">
                      <img
                        src={property.media?.images?.[0]?.url || 'https://via.placeholder.com/400x300?text=No+Image'}
                        alt={property.title}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => navigate(`/property/${property._id || property.id}`)}
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">{property.title}</h3>
                          <p className="text-gray-600 text-sm mb-2">
                            {property.locality}, {property.city}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-700">
                            <span>{property.bhk} BHK</span>
                            <span>•</span>
                            <span>{property.area} {property.areaUnit}</span>
                            <span>•</span>
                            <span className="capitalize">{getLabel(PROPERTY_TYPES, property.propertyType)}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            {formatPrice(property.price)}
                          </div>
                          <span className="text-sm text-gray-600">
                            {property.priceType === 'per_month' ? '/month' : '/night'}
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {property.description}
                      </p>

                      {/* Stats */}
                      <div className="flex items-center space-x-6 text-sm text-gray-600 mb-4">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {property.views || 0} views
                        </div>
                        <div className={`px-2 py-1 rounded text-xs font-semibold ${
                          property.status === 'active' ? 'bg-green-100 text-green-800' :
                          property.status === 'sold' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {property.status.toUpperCase()}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-3 relative z-50 pointer-events-auto">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('View clicked');
                            navigate(`/property/${property._id || property.id}`);
                          }}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition cursor-pointer shadow-sm hover:shadow-md"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Edit clicked');
                            handleEdit(property._id || property.id);
                          }}
                          className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-purple-700 transition cursor-pointer shadow-sm hover:shadow-md"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Delete clicked');
                            handleDelete(property._id || property.id);
                          }}
                          disabled={deleteLoading === (property._id || property.id)}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-sm hover:shadow-md"
                        >
                          {deleteLoading === (property._id || property.id) ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center space-x-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-gray-700">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MyProperties;

